import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { hasPermission } from '@relif/zeller-roles';
import { PrimaryBtn } from 'components/UI/Buttons';
import Conversation from 'components/common/Conversation';
import DocumentViewer from 'components/common/DocumentViewer';
import { onMessage } from 'firebase/messaging';
import { messaging } from 'firebaseConfig';
import useCurrentUser from 'hooks/useCurrentUser';
import useIsMobile from 'hooks/useIsMobile';
import Pusher from 'pusher-js';
import { useGetBusinessUnitByIdMutation } from 'services/api/businessUnits.api';
import {
  useGetClientQuery,
  useUpdateAdminEditableAttributesMutation,
  useGetFinancialAdvisorIndividualClientMetricsMutation,
  useGetClientLeadsQuery,
  useGetClientDocumentsQuery,
} from 'services/api/clients.api';
import { useGetClientMessagesQuery, useGetNewClientMessagesMutation } from 'services/api/messages';
import { useGetTasksByClientQuery } from 'services/api/tasks.api';
import priceFormat from 'services/format/priceFormat';
import { EditableAdminClientAttributes } from 'services/types/client';
import { Message, MessageStatus } from 'services/types/message';
import { ScheduledMessage } from 'services/types/scheduledMessage';

import ChatFooter from './ChatFooter';
import ChatHeader from './ChatHeader';
import EditableAttribute from './EditableAttribute';
import UserAssigner from './UserAssigner';

const ErrorPage = lazy(() => import('pages/Error/ErrorPage'));

const PUSHER_APP_KEY = import.meta.env.VITE_PUSHER_APP_KEY || '';

const Chat = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { isMobile } = useIsMobile();

  const {
    data: client,
    refetch: refetchClient,
    isLoading,
    isFetching,
  } = useGetClientQuery(Number(clientId), {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: messages,
    refetch: refetchMessages,
    isFetching: isLoadingMessages,
  } = useGetClientMessagesQuery(Number(clientId), {
    refetchOnMountOrArgChange: true,
  });
  const [getNewClientMessages, { data: messagesData }] = useGetNewClientMessagesMutation();
  const [getBusinessUnitById, { data: clientDealership }] = useGetBusinessUnitByIdMutation();
  const [updateClient, { data: updatedClient }] = useUpdateAdminEditableAttributesMutation();
  const [getFinancialAdvisorIndividualClientMetrics, { data: financialAdvisorIndividualClientMetrics }] =
    useGetFinancialAdvisorIndividualClientMetricsMutation();
  const { data: clientLeads, refetch: refetchClientLeads } = useGetClientLeadsQuery(Number(clientId), {
    refetchOnMountOrArgChange: true,
  });
  const { data: clientDocuments } = useGetClientDocumentsQuery(Number(clientId), {
    refetchOnMountOrArgChange: true,
  });
  const { refetch: refetchTasks } = useGetTasksByClientQuery(Number(clientId), {
    refetchOnMountOrArgChange: true,
  });

  const [clientDraft, setClientDraft] = useState<EditableAdminClientAttributes | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [conversation, setConversation] = useState<Message[]>([]);
  const clientFiles = conversation.filter((m) => m.role === 'user' && m.fileName);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [isFilteredConversation, setIsFilteredConversation] = useState<boolean>(
    currentUser?.filterConversationsByDefault || false
  );
  const [showAttributes, setShowAttributes] = useState(false);
  const [showDiscardedLeads, setShowDiscardedLeads] = useState(false);
  const isCarDealershipAssistant = clientDealership?.type === 'car_dealership';

  useEffect(() => {
    if (messages) {
      setConversation(messages);
    }
  }, [messages]);

  useEffect(() => {
    if (client) {
      setClientDraft({
        firstName: client.firstName,
        lastName: client.lastName,
        hasValidName: client.hasValidName,
        rut: client.rut || undefined,
        dni: client.dni || undefined,
        email: client.email,
        salary: client.salary,
        savingsAmount: client.savingsAmount || undefined,
        hasVehicleAsPartPayment: client.hasVehicleAsPartPayment,
        requiresFinancing: client.requiresFinancing,
        monthlyPaymentsAmount: client.monthlyPaymentsAmount || undefined,
        budgetAmount: client.budgetAmount || undefined,
        modelsOfInterest: client.modelsOfInterest || [],
        disabledAutomaticResponses: client.disabledAutomaticResponses || false,
        disabledFollowUps: client.disabledFollowUps || false,
        isTestClient: client.isTestClient || false,
        brandOfInterest: client.brandOfInterest || undefined,
        aiSafetyStatus: client.aiSafetyStatus || undefined,
        notes: client.notes || undefined,
      });
      getBusinessUnitById(client.businessUnitId).catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    }
    if (client?.scheduledMessages) {
      setScheduledMessages(client.scheduledMessages);
    }
  }, [client]);

  useEffect(() => {
    if (updatedClient) {
      refetchClient().catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    }
  }, [updatedClient, refetchClient]);

  useEffect(() => {
    if (clientDealership?.type === 'financial_advisor' && client?.id) {
      getFinancialAdvisorIndividualClientMetrics(client?.id).catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    }
  }, [clientDealership, client]);

  const handleUpdateMessageStatus = (
    updatedMessages: { id: number; status: MessageStatus }[],
    refetchStatusMessages: boolean
  ) => {
    setConversation((prevConversation) =>
      prevConversation.map((m) => {
        const newStatus = updatedMessages.find((um) => um.id === m.id)?.status;
        if (newStatus) {
          return { ...m, status: newStatus };
        }
        return m;
      })
    );
    const notFoundMessages = updatedMessages
      .filter((um) => !conversation.find((m) => m.id === um.id))
      .map((um) => um.id);
    if (notFoundMessages.length > 0 && refetchStatusMessages) {
      getNewClientMessages({ clientId: Number(clientId), messageId: Math.min(...notFoundMessages) }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    }
  };

  useEffect(() => {
    if (messagesData) {
      setConversation((prevConversation) => {
        // If a message already exists in the conversation, replace it otherwise, add the new message to the conversation
        const updatedConversation = [...prevConversation];
        return messagesData.newMessages
          .reduce((acc, newMessage) => {
            const existingIndex = acc.findIndex((m) => m.id === newMessage.id);
            if (existingIndex !== -1) {
              acc[existingIndex] = newMessage;
            } else {
              acc.push(newMessage);
            }
            return acc;
          }, updatedConversation)
          .sort((a, b) => a.id - b.id);
      });
      handleUpdateMessageStatus(messagesData.oldMessagesStatuses, false);
    }
  }, [messagesData]);

  useEffect(() => {
    if (client) {
      const pusher = new Pusher(PUSHER_APP_KEY, { cluster: 'sa1' });
      const channel = pusher.subscribe(`chat-${client.id}`);
      channel.bind('new-message', (data: { messageId: number }) => {
        getNewClientMessages({ clientId: Number(clientId), messageId: data.messageId }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
      });
      channel.bind('update-messages', (data: { messages: { id: number; status: MessageStatus }[] }) => {
        handleUpdateMessageStatus(data.messages, true);
      });
      return () => {
        channel.unbind_all();
        pusher.unsubscribe(`chat-${client.id}`);
        pusher.disconnect();
      };
    }
    return () => {};
  }, [client]);

  const handleEditClient = () => {
    setIsEditing(!isEditing);
  };

  const handleSaveClient = () => {
    if (clientDraft && client) {
      updateClient({ id: client.id, body: clientDraft }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
      setIsEditing(false);
    }
  };

  const handleRefetchClient = () => {
    // eslint-disable-next-line no-console
    refetchClient().catch((err) => console.error(err));
    // eslint-disable-next-line no-console
    refetchMessages().catch((err) => console.error(err));
    // eslint-disable-next-line no-console
    refetchClientLeads().catch((err) => console.error(err));
    // eslint-disable-next-line no-console
    refetchTasks().catch((err) => console.error(err));
  };

  if (messaging) {
    onMessage(messaging, (payload) => {
      // eslint-disable-next-line no-console
      console.log('Message received in client:', payload);
      if (payload.data?.identifier === 'new-client-message') {
        refetchMessages().catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
        const notificationAudio = new Audio('/new-message-sound.wav');
        notificationAudio.play().catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
      }
    });
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Suspense fallback={<CircularProgress />}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{
            maxWidth: 1000,
            mx: 'auto',
            p: 4,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              mb: 6,
            }}
          >
            No se encontró el cliente
          </Typography>
          <Typography variant="h6">Algunas razones posibles son:</Typography>
          <ul>
            <li>El cliente no existe</li>
            <li>No tienes permisos para acceder a este cliente</li>
            <li>El cliente está asignado a otro ejecutivo y no tienes acceso</li>
          </ul>
          <ErrorPage />
        </Box>
      </Suspense>
    );
  }

  return (
    <>
      <Toolbar sx={{ px: { xs: 2, md: 0 } }}>
        <Box
          pl={{ xs: 5, md: 0 }}
          py={2}
          display="flex"
          alignItems="center"
          width="100%"
          justifyContent="space-between"
        >
          <div>
            <IconButton
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/clients');
                }
              }}
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
            {currentUser?.isAdmin && (showAttributes || !isMobile) && (
              <IconButton onClick={handleEditClient} size="large">
                {isEditing ? <CancelIcon /> : <EditIcon />}
              </IconButton>
            )}
            {showAttributes && isMobile && <Button onClick={() => setShowAttributes(false)}>Conversación</Button>}
            {!showAttributes && isMobile && <Button onClick={() => setShowAttributes(true)}>Cliente</Button>}
          </div>
          <div>
            {(!showAttributes || !isMobile) && (
              <ChatHeader
                client={client}
                isLoading={isFetching || isLoadingMessages}
                refetch={handleRefetchClient}
                setIsFilteredConversation={setIsFilteredConversation}
                isFilteredConversation={isFilteredConversation}
                isLastLeadClosed={[...(clientLeads?.leads ?? [])].sort((l) => l.id)[0]?.status === 'CLOSED'}
              />
            )}
          </div>
        </Box>
      </Toolbar>

      <Grid container>
        {(showAttributes || !isMobile) && (
          <Grid item xs={12} md={4} borderRight="1px solid #e0e0e0" height="calc(100vh - 80px)" display="inline-grid">
            <Box overflow="auto" height="100%" px={2} py={1}>
              <Grid item>
                <b>Identificador:</b> {client.id} {currentUser?.isAdmin && `(${client.businessUnit?.name})`}
              </Grid>
              <Box display="flex" justifyContent="flex-start" alignItems="center">
                {clientDraft?.hasValidName && <CheckIcon color="success" />}
                <EditableAttribute
                  label="Nombre"
                  value={clientDraft?.firstName || ''}
                  onChange={(e) => setClientDraft({ ...clientDraft, firstName: e.target.value })}
                  disabled={!isEditing}
                  type="text"
                />
              </Box>
              <EditableAttribute
                label="Apellido"
                value={clientDraft?.lastName || ''}
                onChange={(e) => setClientDraft({ ...clientDraft, lastName: e.target.value })}
                disabled={!isEditing}
                type="text"
              />
              <EditableAttribute
                label="Email"
                value={clientDraft?.email || ''}
                onChange={(e) => setClientDraft({ ...clientDraft, email: e.target.value })}
                disabled={!isEditing}
                type="email"
              />
              {client.dni ? (
                <EditableAttribute
                  label="DNI"
                  value={clientDraft?.dni || ''}
                  onChange={(e) => setClientDraft({ ...clientDraft, dni: e.target.value })}
                  disabled={!isEditing}
                  type="text"
                />
              ) : (
                <EditableAttribute
                  label="Rut"
                  value={clientDraft?.rut || ''}
                  onChange={(e) => setClientDraft({ ...clientDraft, rut: e.target.value })}
                  disabled={!isEditing}
                  type="text"
                />
              )}
              <Grid item>
                <b>Teléfono:</b> {client.phone}
              </Grid>
              <EditableAttribute
                label="Desactivar respuestas automáticas"
                value={clientDraft?.disabledAutomaticResponses ? '🚫🤖 Si' : false}
                onChange={(e) => setClientDraft({ ...clientDraft, disabledAutomaticResponses: e.target.checked })}
                disabled={
                  !isEditing ||
                  ['ASSIGNED_TO_SALESMAN', 'PENDING_TO_BE_HANDLED', 'HANDLED_BY_SALESMAN'].includes(
                    client.lastLeadStatus || ''
                  )
                }
                type="checkbox"
              />
              <EditableAttribute
                label="Sueldo"
                value={clientDraft?.salary?.toString() || ''}
                onChange={(e) => setClientDraft({ ...clientDraft, salary: Number(e.target.value) })}
                disabled={!isEditing}
                type="price"
              />
              <Grid item>
                <b>Mensaje inicial:</b> {client.prospectMessage}
              </Grid>
              <Grid item>
                <b>Origen:</b> {client.source}
              </Grid>
              <Grid item>
                <b>Temas de conversación:</b> {client.topics?.join(', ') || '-'}
              </Grid>
              {financialAdvisorIndividualClientMetrics && (
                <Grid item>
                  <b>Completó el escáner:</b>{' '}
                  {financialAdvisorIndividualClientMetrics.hasOnboardingComplete ? 'Si' : 'No'}
                </Grid>
              )}
              {financialAdvisorIndividualClientMetrics && (
                <Grid item>
                  <b>Está en Dicom:</b> {client.customAttributes?.hasUnpaidDebts ? 'Si' : 'No'}
                </Grid>
              )}
              {financialAdvisorIndividualClientMetrics && (
                <Grid item>
                  <b>Hizo una solicitud de refinanciamiento:</b>{' '}
                  {financialAdvisorIndividualClientMetrics.hasRefinanceRequest ? 'Si' : 'No'}
                </Grid>
              )}
              {financialAdvisorIndividualClientMetrics && (
                <Grid item>
                  <b>Recibió ofertas:</b> {financialAdvisorIndividualClientMetrics.hasOffers ? 'Si' : 'No'}
                </Grid>
              )}
              {isCarDealershipAssistant && (
                <>
                  <EditableAttribute
                    label="Pie"
                    value={clientDraft?.savingsAmount?.toString() || ''}
                    onChange={(e) => setClientDraft({ ...clientDraft, savingsAmount: Number(e.target.value) })}
                    disabled={!isEditing}
                    type="price"
                  />
                  <EditableAttribute
                    label="¿Vehículo en parte de pago?"
                    value={!!clientDraft?.hasVehicleAsPartPayment}
                    onChange={(e) => setClientDraft({ ...clientDraft, hasVehicleAsPartPayment: e.target.checked })}
                    disabled={!isEditing}
                    type="checkbox"
                  />
                  <EditableAttribute
                    label="¿Requiere financiamiento?"
                    value={!!clientDraft?.requiresFinancing}
                    onChange={(e) => setClientDraft({ ...clientDraft, requiresFinancing: e.target.checked })}
                    disabled={!isEditing}
                    type="checkbox"
                  />
                  <EditableAttribute
                    label="Modelos de interés"
                    value={clientDraft?.modelsOfInterest?.join(', ') || ''}
                    onChange={(e) => setClientDraft({ ...clientDraft, modelsOfInterest: e.target.value.split(', ') })}
                    disabled={!isEditing}
                    type="text"
                  />
                  <EditableAttribute
                    label="Marca de interés"
                    value={clientDraft?.brandOfInterest || ''}
                    onChange={(e) => setClientDraft({ ...clientDraft, brandOfInterest: e.target.value })}
                    disabled={!isEditing}
                    type="text"
                  />
                  <EditableAttribute
                    label="Presupuesto"
                    value={clientDraft?.budgetAmount || 0}
                    onChange={(e) => setClientDraft({ ...clientDraft, budgetAmount: Number(e.target.value) })}
                    disabled={!isEditing}
                    type="price"
                  />
                  <Grid item>
                    <b>Fecha de visita:</b>{' '}
                    {client.visitDate ? new Date(client.visitDate).toLocaleString('es-CL') : 'No'}
                  </Grid>
                  <Grid item>
                    <b>Estado de crédito:</b> {client.creditStatus}
                  </Grid>
                  {client.businessUnit?.hasSellerManagement &&
                    currentUser &&
                    hasPermission(currentUser, 'clients', 'assign', client) && (
                      <Grid item>
                        <UserAssigner client={client} />
                      </Grid>
                    )}
                  {client.pilotAssignedUser && (
                    <Grid item>
                      <b>Usuario asignado en Pilot:</b> {client.pilotAssignedUser}
                    </Grid>
                  )}
                </>
              )}

              {/* Insurance fields for MUEVE SEGURO clients with insurance_broker business units */}
              {clientDealership?.type === 'insurance_broker' && (
                <>
                  {(() => {
                    const insurexData = client.customAttributes?.insurexData;
                    return (
                      insurexData &&
                      typeof insurexData === 'object' && (
                        <>
                          {'loanProtectionInsurance' in insurexData &&
                            insurexData.loanProtectionInsurance?.externalId && (
                              <Grid item>
                                <b>ID Monday Desgravamen:</b> {insurexData.loanProtectionInsurance.externalId}
                              </Grid>
                            )}
                          {'loanProtectionInsurance' in insurexData &&
                            insurexData.loanProtectionInsurance?.simulationExternalId && (
                              <Grid item>
                                <b>ID Simulación Desgravamen:</b>{' '}
                                {insurexData.loanProtectionInsurance.simulationExternalId}
                              </Grid>
                            )}
                          {'unemploymentInsurance' in insurexData && insurexData.unemploymentInsurance?.externalId && (
                            <Grid item>
                              <b>ID Monday Cesantía:</b> {insurexData.unemploymentInsurance.externalId}
                            </Grid>
                          )}
                          {'unemploymentInsurance' in insurexData &&
                            insurexData.unemploymentInsurance?.simulationExternalId && (
                              <Grid item>
                                <b>ID Simulación Cesantía:</b> {insurexData.unemploymentInsurance.simulationExternalId}
                              </Grid>
                            )}
                        </>
                      )
                    );
                  })()}
                  {client.businessUnit?.hasSellerManagement &&
                    currentUser &&
                    hasPermission(currentUser, 'clients', 'assign', client) && (
                      <Grid item>
                        <UserAssigner client={client} />
                      </Grid>
                    )}
                </>
              )}

              {clientLeads?.leads && clientLeads.leads.length > 0 && (
                <Grid item>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography component="span" fontWeight="bold">
                      Prospectos:
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" mr={1}>
                        Mostrar descartados
                      </Typography>
                      <Switch
                        size="small"
                        onChange={(e) => setShowDiscardedLeads(e.target.checked)}
                        checked={showDiscardedLeads}
                      />
                    </Box>
                  </Box>
                  {clientLeads.leads
                    .filter((lead) => showDiscardedLeads || !lead.discarded)
                    .map((lead) => (
                      <Accordion key={lead.id} style={{ backgroundColor: lead.discarded ? '#f0f0f0' : 'inherit' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box>
                            <Typography>{lead.source}</Typography>
                            <Typography variant="caption">
                              {new Date(lead.createdAt).toLocaleString('es-CL')}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography>
                            <b>Marca de interés:</b> {lead.initialBrandOfInterest}
                          </Typography>
                          <Typography>
                            <b>Modelo de interés:</b> {lead.initialModelOfInterest}
                          </Typography>
                          <Typography>
                            <b>Mensaje inicial:</b> {lead.initialMessage}
                          </Typography>
                          {lead.source && (
                            <Typography>
                              <b>Origen:</b> {lead.source}
                            </Typography>
                          )}
                          {lead.outboundCampaignId && (
                            <Typography>
                              <b>Campaña:</b> {lead.outboundCampaignId}
                            </Typography>
                          )}
                          <Typography>
                            <b>Subido al CRM:</b>{' '}
                            {lead.CRMUpdateAt ? new Date(lead.CRMUpdateAt).toLocaleString('es-CL') : 'No'}
                          </Typography>
                          {lead.status && (
                            <Typography>
                              <b>Estado:</b> {lead.status}
                            </Typography>
                          )}
                          {lead.AIComment && (
                            <Typography>
                              <b>Comentario de AI:</b> {lead.AIComment}
                            </Typography>
                          )}
                          {lead.url && (
                            <Typography noWrap>
                              <b>URL:</b>{' '}
                              <a
                                href={lead.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                              >
                                {lead.url}
                              </a>
                            </Typography>
                          )}
                          {lead.UTM && (
                            <>
                              {lead.UTM.source && (
                                <Typography>
                                  <b>UTM_source:</b> {lead.UTM.source}
                                </Typography>
                              )}
                              {lead.UTM.medium && (
                                <Typography>
                                  <b>UTM_medium:</b> {lead.UTM.medium}
                                </Typography>
                              )}
                              {lead.UTM.campaign && (
                                <Typography>
                                  <b>UTM_campaign:</b> {lead.UTM.campaign}
                                </Typography>
                              )}
                              {lead.UTM.content && (
                                <Typography>
                                  <b>UTM_content:</b> {lead.UTM.content}
                                </Typography>
                              )}
                              {lead.UTM.term && (
                                <Typography>
                                  <b>UTM_term:</b> {lead.UTM.term}
                                </Typography>
                              )}
                            </>
                          )}
                          {lead.visitDate && (
                            <Grid item>
                              <b>Fecha de visita:</b> {new Date(lead.visitDate).toLocaleString('es-CL')}
                            </Grid>
                          )}
                          {lead.webhookBodyAtCreation && (
                            <div>
                              <Button
                                onClick={() => {
                                  const dataStr = JSON.stringify(lead.webhookBodyAtCreation, null, 2);
                                  const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
                                  const downloadAnchorNode = document.createElement('a');
                                  downloadAnchorNode.setAttribute('href', dataUri);
                                  downloadAnchorNode.setAttribute(
                                    'download',
                                    `webhook_body_at_creation_${lead.id}.json`
                                  );
                                  document.body.appendChild(downloadAnchorNode);
                                  downloadAnchorNode.click();
                                  downloadAnchorNode.remove();
                                }}
                                endIcon={<DownloadIcon />}
                                color="inherit"
                                size="small"
                                variant="outlined"
                              >
                                Payload de creación en Zeller
                              </Button>
                            </div>
                          )}
                          {lead.CRMCreationPayload && (
                            <div>
                              <Button
                                onClick={() => {
                                  const dataStr = JSON.stringify(lead.CRMCreationPayload, null, 2);
                                  const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
                                  const downloadAnchorNode = document.createElement('a');
                                  downloadAnchorNode.setAttribute('href', dataUri);
                                  downloadAnchorNode.setAttribute('download', `crm_payload_creation_${lead.id}.json`);
                                  document.body.appendChild(downloadAnchorNode);
                                  downloadAnchorNode.click();
                                  downloadAnchorNode.remove();
                                }}
                                endIcon={<DownloadIcon />}
                                color="inherit"
                                size="small"
                                variant="outlined"
                              >
                                Payload de creación en CRM
                              </Button>
                            </div>
                          )}
                          {lead.CRMCreationResponse && (
                            <div>
                              <Button
                                onClick={() => {
                                  const dataStr = JSON.stringify(lead.CRMCreationResponse, null, 2);
                                  const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
                                  const downloadAnchorNode = document.createElement('a');
                                  downloadAnchorNode.setAttribute('href', dataUri);
                                  downloadAnchorNode.setAttribute('download', `crm_creation_response_${lead.id}.json`);
                                  document.body.appendChild(downloadAnchorNode);
                                  downloadAnchorNode.click();
                                  downloadAnchorNode.remove();
                                }}
                                endIcon={<DownloadIcon />}
                                color="inherit"
                                size="small"
                                variant="outlined"
                              >
                                Respuesta de creación en CRM
                              </Button>
                            </div>
                          )}
                          {lead.crmUploadHistory && lead.crmUploadHistory.length > 0 && (
                            <div>
                              <Button
                                onClick={() => {
                                  const dataStr = JSON.stringify(lead.crmUploadHistory, null, 2);
                                  const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
                                  const downloadAnchorNode = document.createElement('a');
                                  downloadAnchorNode.setAttribute('href', dataUri);
                                  downloadAnchorNode.setAttribute('download', `crm_upload_history_${lead.id}.json`);
                                  document.body.appendChild(downloadAnchorNode);
                                  downloadAnchorNode.click();
                                  downloadAnchorNode.remove();
                                }}
                                endIcon={<DownloadIcon />}
                                color="inherit"
                                size="small"
                                variant="outlined"
                              >
                                Historial de actualizaciones CRM ({lead.crmUploadHistory.length})
                              </Button>
                            </div>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </Grid>
              )}

              {clientFiles.length > 0 && (
                <Grid item>
                  <b>Archivos:</b>
                  {clientFiles.map((file) => (
                    <Box key={file.id} display="flex" alignItems="center" gap={1}>
                      <Link
                        component="a"
                        href={file.fileUrl || ''}
                        target="_blank"
                        sx={{
                          maxWidth: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                          verticalAlign: 'bottom',
                          direction: 'rtl',
                          textAlign: 'left',
                        }}
                      >
                        {file.fileName}
                      </Link>
                    </Box>
                  ))}
                </Grid>
              )}

              {/* Documents Section */}
              <Grid item>
                <b>Documentos:</b>
                <DocumentViewer documents={clientDocuments || []} clientId={Number(clientId)} />
              </Grid>

              {currentUser?.isAdmin && (
                <>
                  {clientDealership?.type !== 'financial_advisor' && (
                    <EditableAttribute
                      label="Follow ups desactivados"
                      value={!!clientDraft?.disabledFollowUps}
                      onChange={(e) => setClientDraft({ ...clientDraft, disabledFollowUps: e.target.checked })}
                      disabled={!isEditing}
                      type="checkbox"
                    />
                  )}
                  <EditableAttribute
                    label="Cliente de prueba"
                    value={!!clientDraft?.isTestClient}
                    onChange={(e) => setClientDraft({ ...clientDraft, isTestClient: e.target.checked })}
                    disabled={!isEditing}
                    type="checkbox"
                  />
                  {client.branchOffice && (
                    <Grid item>
                      <b>Sucursal:</b> {client.branchOffice}
                    </Grid>
                  )}
                  {clientDealership?.type !== 'financial_advisor' && (
                    <>
                      <Grid item>
                        <b>Subido a CRM:</b> {client.lastCRMUpdate !== null ? 'Si' : 'No'}
                      </Grid>
                      <Grid item>
                        <b>Última actualización en CRM:</b>{' '}
                        {client.lastCRMUpdate ? new Date(client.lastCRMUpdate).toLocaleString('es-CL') : '-'}
                      </Grid>

                      <Grid item>
                        <b>Tipo:</b>{' '}
                        {client.clientType
                          ? {
                              buy: 'Compra',
                              sell: 'Venta',
                              'after-sale': 'Post-Venta',
                            }[client.clientType] || '-'
                          : '-'}
                      </Grid>
                    </>
                  )}
                  <EditableAttribute
                    label="Notas"
                    value={clientDraft?.notes || ''}
                    onChange={(e) => setClientDraft({ ...clientDraft, notes: e.target.value })}
                    disabled={!isEditing}
                    type="text"
                  />
                  <Grid item>
                    <b>Motivo de término:</b> {client.endReason}
                  </Grid>
                  <Grid item>
                    <b>Revisión chat:</b> {client.AIRevision}
                  </Grid>
                  {!isFilteredConversation && (
                    <>
                      <Grid item>
                        <b>Costo Total:</b> {priceFormat.format(client.cost || 0)}
                      </Grid>
                      <EditableAttribute
                        label="Safety Status (safe, unsafe or force-always-safe)"
                        value={clientDraft?.aiSafetyStatus || ''}
                        onChange={(e) => setClientDraft({ ...clientDraft, aiSafetyStatus: e.target.value })}
                        disabled={!isEditing}
                        type="text"
                      />
                    </>
                  )}
                </>
              )}
              {isEditing && (
                <Grid item xs={12}>
                  <PrimaryBtn onClick={handleSaveClient} variant="contained">
                    Guardar
                  </PrimaryBtn>
                </Grid>
              )}
              {client.surveys?.map((survey) => (
                <Grid item key={survey.name}>
                  <b>Resultado encuesta:</b> {survey.name} ({survey.status === 'finished' ? '✅' : 'Sin terminar'})
                  {survey.results.map((result, index) => (
                    <Grid item pl={1} key={result.question}>
                      {index + 1}. {result.question}: <b>{result.response || '-'}</b>
                    </Grid>
                  ))}
                </Grid>
              ))}
            </Box>
          </Grid>
        )}
        {(!showAttributes || !isMobile) && (
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              backgroundImage: 'url("/whatsapp-background.png")',
              backgroundColor: '#efeae2',
              backgroundRepeat: 'repeat',
              backgroundSize: '412.5px 749.25px',
              height: 'calc(100vh - 80px)', // 80px is the height of the toolbar
              display: 'grid',
            }}
          >
            {isLoadingMessages && (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            )}
            {!isLoadingMessages && conversation.length > 0 && (
              <Box overflow="auto" height="100%">
                <Conversation
                  conversation={conversation}
                  isFilteredConversation={isFilteredConversation}
                  fullHeight
                  scheduledMessages={scheduledMessages}
                />
              </Box>
            )}
            <Box alignSelf="end">
              <ChatFooter
                client={client}
                conversation={conversation}
                setConversation={setConversation}
                addScheduledMessage={(message) =>
                  setScheduledMessages((curr) => [...curr, message] as ScheduledMessage[])
                }
                onRefetchClient={handleRefetchClient}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Chat;
